import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Users, ArrowRight, PenLine, ImagePlus, MapPin, Award, ShieldCheck, Globe, Loader2, Trash2, Plus, Eye, X, Map, BookOpen, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { StatBar } from "@/components/StatBar";
import { MEDIA } from "@/lib/media";

const CAPS = [
  { icon: PenLine, title: "Write a Story", desc: "Share your hunt with the community." },
  { icon: ImagePlus, title: "Add Photos", desc: "Upload and organize your hunt photos." },
  { icon: MapPin, title: "Track Hunts", desc: "Keep a record of every adventure." },
  { icon: Award, title: "Build Legacy", desc: "Your stories today. Their inspiration tomorrow." },
];

// Demo Archive stories — clearly labelled, shown only until real published stories exist.
const DEMO_STORIES = [
  { id: "d1", species: "Elk", general_location: "Montana", title: "Chasing September", body: "A cold front, a bugle, and a once-in-a-lifetime morning.", author_name: "Tyler M.", img: MEDIA.exploreField, demo: true },
  { id: "d2", species: "Mule Deer", general_location: "Wyoming", title: "Rimrock Surprise", body: "How glassing one more ridge changed everything.", author_name: "Jordan K.", img: MEDIA.exploreOutfitters, demo: true },
  { id: "d3", species: "Black Bear", general_location: "Maine", title: "Spring in the North", body: "Bait, patience, and a heavy boar at last light.", author_name: "Mark D.", img: MEDIA.exploreAI, demo: true },
  { id: "d4", species: "Waterfowl", general_location: "Arkansas", title: "Sunrise Limits", body: "A flooded timber hole and a perfect line of birds.", author_name: "Caleb S.", img: MEDIA.exploreRecipes, demo: true },
];

export default function FieldNotes({ embedded = false }) {
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [stories, setStories] = useState(null);

  useEffect(() => {
    api.publicJournal().then((d) => setStories(Array.isArray(d) ? d : [])).catch(() => setStories([]));
  }, []);

  const realStories = (stories || []).map((s, i) => ({ ...s, img: (s.photos && s.photos[0]) || [MEDIA.exploreField, MEDIA.exploreOutfitters, MEDIA.exploreAI, MEDIA.exploreRecipes][i % 4] }));
  const displayStories = realStories.length ? realStories : DEMO_STORIES;
  const usingDemo = realStories.length === 0;
  const notesHref = embedded ? "/field-notes" : "#my-notes";
  // The Archive is its own dedicated social platform page — always route there (never scroll/embed).
  const archiveHref = "/archive";

  return (
    <div className={embedded ? "bg-paper" : "bg-paper min-h-screen"} data-testid="field-notes-page">
      {!embedded && <SiteHeader />}

      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden grain" data-testid="fn-hero">
        <div className="absolute inset-0 z-0">
          <img src={MEDIA.fieldnotesHero} alt="Hunter writing in a field journal overlooking a mountain valley" className="w-full h-full object-cover object-right" loading={embedded ? "lazy" : "eager"} />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/80 to-charcoal/10" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 py-20 lg:py-28 max-w-3xl">
          <span className="hr-overline text-blaze">Field Notes</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight mt-4">Every hunt<br />leaves a story.</h1>
          <p className="text-lg text-bone/80 mt-5 max-w-md">Document the miles, the misses, the moments, and the memories.</p>
          <a href={notesHref} className="mt-8 inline-flex items-center gap-2 bg-bone text-charcoal px-6 py-3.5 font-semibold text-sm hover:bg-white transition-colors" data-testid="fn-explore">
            Explore Field Notes <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-14 space-y-16">
        {/* Two feature cards */}
        <section className="grid md:grid-cols-2 gap-5" data-testid="fn-feature-cards">
          <FeatureCard icon={Lock} img={MEDIA.fieldnotesMyNotes} title="My Field Notes" sub="Your private hunting journal."
            body="Track your hunts, locations, conditions, gear, harvests and more — all in one private place."
            cta="Go to My Notes" href={notesHref} testid="fn-card-mynotes" />
          <FeatureCard icon={Users} img={MEDIA.exploreField} title="The Archive" sub="Stories from the HomeRange community."
            body="Read published hunt stories, lessons, photos and traditions from hunters across the country."
            cta="Explore the Archive" href={archiveHref} testid="fn-card-archive" />
        </section>

        {/* From the Archive */}
        <section id="archive" data-testid="fn-archive" className="scroll-mt-24">
          <div className="flex items-end justify-between gap-3">
            <h2 className="font-serif text-3xl sm:text-4xl text-forest tracking-tight">From the Archive</h2>
            <a href={archiveHref} className="inline-flex items-center gap-1.5 text-blaze font-semibold text-sm hover:underline" data-testid="fn-view-all">View all stories <ArrowRight size={14} /></a>
          </div>
          {usingDemo && <span className="text-[10px] uppercase tracking-wider text-earth/70 border border-earth/30 px-2 py-1 rounded-sm inline-block mt-2" data-testid="archive-demo-badge">Sample stories</span>}
          {stories === null ? (
            <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
              {displayStories.slice(0, 8).map((s) => <StoryCard key={s.id} s={s} />)}
            </div>
          )}
        </section>

        {/* My Field Notes management (full page only) */}
        {!embedded && (
        <section id="my-notes" data-testid="fn-my-notes" className="scroll-mt-24">
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-forest" />
            <h2 className="font-serif text-3xl sm:text-4xl text-forest tracking-tight">My Field Notes</h2>
            <span className="text-[11px] uppercase tracking-wider text-charcoal/45 border border-saddle/25 px-2 py-0.5 rounded-sm ml-1">Private by default</span>
          </div>
          <p className="text-charcoal/60 text-sm mt-2 max-w-xl">Only you can see your notes. Exact locations and waypoints are never shared. Publish a note to The Archive only when you choose to.</p>
          {isAuthed ? <MyNotes /> : (
            <div className="mt-6 border border-dashed border-saddle/30 p-10 text-center" data-testid="fn-auth-gate">
              <p className="text-charcoal/60">Sign in to keep your private hunting journal.</p>
              <button onClick={() => openAuth("register")} className="mt-4 bg-blaze text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-light transition-colors" data-testid="fn-signin">Sign in / Create account</button>
            </div>
          )}
        </section>
        )}
      </main>

      {/* Capability strip */}
      <section className="bg-charcoal text-bone" data-testid="fn-capabilities">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 lg:grid-cols-4 gap-y-8">
          {CAPS.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className={`px-4 lg:px-8 ${i > 0 ? "lg:border-l border-white/10" : ""}`}>
                <Icon size={26} strokeWidth={1.5} className="text-blaze" />
                <p className="font-serif text-xl mt-3">{c.title}</p>
                <p className="text-bone/55 text-sm mt-1">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <StatBar items={[
        { icon: Map, value: "50+", label: "States Represented" },
        { icon: BookOpen, value: "100K+", label: "Stories & Journals" },
        { icon: Camera, value: "1M+", label: "Photos Shared" },
        { icon: ShieldCheck, value: "Verified", label: "Real People. Real Hunts." },
      ]} />

      {!embedded && <Footer />}
    </div>
  );
}

function FeatureCard({ icon: Icon, img, title, sub, body, cta, href, testid }) {
  return (
    <a href={href} className="group relative min-h-[300px] overflow-hidden flex flex-col justify-end p-7 bg-charcoal" data-testid={testid}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/60 to-charcoal/30" />
      <div className="relative">
        <span className="w-12 h-12 rounded-full bg-bone grid place-items-center text-charcoal mb-4"><Icon size={20} /></span>
        <h3 className="font-serif text-3xl text-bone">{title}</h3>
        <p className="text-bone/80 mt-1">{sub}</p>
        <p className="text-bone/60 text-sm mt-2 max-w-sm">{body}</p>
        <span className="inline-flex items-center gap-2 mt-4 bg-bone/90 text-charcoal px-4 py-2 text-sm font-semibold group-hover:bg-bone transition-colors">{cta} <ArrowRight size={15} /></span>
      </div>
    </a>
  );
}

function StoryCard({ s }) {
  return (
    <article className="group relative h-[320px] overflow-hidden flex flex-col justify-end bg-charcoal" data-testid={s.demo ? `story-card-demo-${s.id}` : `story-card-${s.id}`}>
      <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/40 to-transparent" />
      <div className="relative p-5">
        {(s.species || s.general_location) && (
          <span className="hr-overline text-blaze text-[10px]">{[s.species, s.general_location].filter(Boolean).join(" · ")}</span>
        )}
        <h3 className="font-serif text-2xl text-bone leading-tight mt-1">{s.title}</h3>
        {s.body && <p className="text-bone/70 text-sm mt-1.5 line-clamp-2">{s.body}</p>}
        <p className="text-bone/50 text-xs mt-3">By {s.author_name || "A HomeRange hunter"}</p>
      </div>
    </article>
  );
}

function MyNotes() {
  const [notes, setNotes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", species: "", general_location: "" });
  const [busy, setBusy] = useState(false);
  const [studioNote, setStudioNote] = useState(null);

  const load = () => api.journal().then(setNotes).catch(() => setNotes([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give your note a title."); return; }
    setBusy(true);
    try { await api.createJournal({ ...form, visibility: "private" }); toast.success("Field note saved (private)"); setForm({ title: "", body: "", species: "", general_location: "" }); setCreating(false); await load(); }
    catch { toast.error("Could not save note"); } finally { setBusy(false); }
  };

  const makePrivate = async (n) => {
    try { await api.updateJournal(n.id, { visibility: "private" }); toast.success("Made private"); await load(); }
    catch { toast.error("Could not update"); }
  };

  const remove = async (id) => { await api.deleteJournal(id); await load(); };

  const inp = "w-full border border-saddle/25 bg-white px-3 py-2.5 text-sm outline-none focus:border-forest";

  return (
    <div className="mt-6" data-testid="my-notes-panel">
      {!creating ? (
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-forest text-bone px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-blaze transition-colors" data-testid="note-new"><Plus size={15} /> New field note</button>
      ) : (
        <div className="bg-white border border-saddle/20 p-5 grid gap-3 max-w-2xl" data-testid="note-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Opening morning, Unit 316)" className={inp} data-testid="note-title" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} placeholder="Species (optional)" className={inp} data-testid="note-species" />
            <input value={form.general_location} onChange={(e) => setForm({ ...form, general_location: e.target.value })} placeholder="General area (never exact — e.g. Gallatin Range)" className={inp} data-testid="note-location" />
          </div>
          <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What happened out there…" className={inp} data-testid="note-body" />
          <div className="flex gap-2">
            <button onClick={create} disabled={busy} className="bg-blaze text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-light transition-colors disabled:opacity-60" data-testid="note-save">{busy ? "Saving…" : "Save note"}</button>
            <button onClick={() => setCreating(false)} className="px-5 py-2.5 text-sm font-semibold text-charcoal/60 hover:text-forest">Cancel</button>
          </div>
        </div>
      )}

      {notes === null ? (
        <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : notes.length === 0 ? (
        <p className="text-charcoal/55 mt-6" data-testid="notes-empty">No field notes yet. Your first entry starts your private journal.</p>
      ) : (
        <div className="grid gap-3 mt-6" data-testid="notes-list">
          {notes.map((n) => (
            <div key={n.id} className="bg-white border border-saddle/20 p-4 flex items-start justify-between gap-4" data-testid={`note-${n.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-serif text-lg text-forest">{n.title}</h4>
                  {n.visibility === "public"
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#2E7D46] border border-[#2E7D46]/30 px-1.5 py-0.5"><Globe size={10} /> In Archive</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-charcoal/50 border border-saddle/25 px-1.5 py-0.5"><Lock size={10} /> Private</span>}
                </div>
                <p className="text-xs text-charcoal/50 mt-1">{[n.species, n.general_location, n.entry_date].filter(Boolean).join(" · ")}</p>
                {n.body && <p className="text-sm text-charcoal/70 mt-1.5 line-clamp-2">{n.body}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {n.visibility === "public"
                  ? <button onClick={() => makePrivate(n)} className="text-xs font-semibold text-charcoal/60 hover:text-forest hover:underline whitespace-nowrap" data-testid={`note-unpublish-${n.id}`}>Make private</button>
                  : <button onClick={() => setStudioNote(n)} className="text-xs font-semibold text-blaze hover:underline whitespace-nowrap" data-testid={`note-publish-${n.id}`}>Turn into story</button>}
                <button onClick={() => remove(n.id)} className="text-charcoal/40 hover:text-[#B04A3A] transition-colors p-1" aria-label="Delete note" data-testid={`note-delete-${n.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {studioNote && <StoryStudio note={studioNote} onClose={() => setStudioNote(null)} onDone={async () => { setStudioNote(null); await load(); }} />}
    </div>
  );
}

function StoryStudio({ note, onClose, onDone }) {
  const [draft, setDraft] = useState({
    title: note.title || "", body: note.body || "", species: note.species || "", general_location: note.general_location || "",
    includeSpecies: !!note.species, includeLocation: !!note.general_location,
  });
  const [photos, setPhotos] = useState(note.photos || []);
  const [photoUrl, setPhotoUrl] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  const addPhoto = () => {
    const u = photoUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) { toast.error("Enter a valid image URL (https://…)"); return; }
    setPhotos((p) => [...p, u]); setPhotoUrl("");
  };

  const publish = async () => {
    if (!draft.title.trim()) { toast.error("Your story needs a title."); return; }
    setBusy(true);
    try {
      await api.updateJournal(note.id, {
        title: draft.title, body: draft.body,
        species: draft.includeSpecies ? draft.species : "",
        general_location: draft.includeLocation ? draft.general_location : "",
        photos, visibility: "public",
      });
      toast.success("Published to The Archive");
      onDone();
    } catch { toast.error("Could not publish"); } finally { setBusy(false); }
  };

  const inp = "w-full border border-saddle/25 bg-white px-3 py-2.5 text-sm outline-none focus:border-forest";
  const story = {
    id: "preview", title: draft.title || "Untitled story", body: draft.body,
    species: draft.includeSpecies ? draft.species : "", general_location: draft.includeLocation ? draft.general_location : "",
    author_name: "You", img: photos[0] || MEDIA.exploreField,
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="story-studio">
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-paper max-h-[92vh] overflow-y-auto hr-scroll">
        <div className="flex items-center justify-between p-5 border-b border-saddle/20 sticky top-0 bg-paper z-10">
          <div>
            <span className="hr-overline text-blaze">Turn into a story</span>
            <h3 className="font-serif text-2xl text-forest">Share it with The Archive</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="studio-close"><X size={22} className="text-charcoal/60" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-charcoal/55 bg-white border border-saddle/20 p-3">Only what you include here becomes public. HomeRange never publishes exact coordinates or waypoints — share a general area only.</p>

          {!preview ? (
            <>
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-charcoal/50">Story title</span>
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={inp} data-testid="studio-title" /></label>
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-charcoal/50">Story</span>
                <textarea rows={5} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className={inp} placeholder="Tell the story…" data-testid="studio-body" /></label>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="border border-saddle/20 bg-white p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-forest">
                    <input type="checkbox" checked={draft.includeSpecies} onChange={(e) => setDraft({ ...draft, includeSpecies: e.target.checked })} data-testid="studio-include-species" /> Include species
                  </label>
                  <input value={draft.species} onChange={(e) => setDraft({ ...draft, species: e.target.value })} disabled={!draft.includeSpecies} className={`${inp} mt-2 disabled:opacity-50`} placeholder="Elk" data-testid="studio-species" />
                </div>
                <div className="border border-saddle/20 bg-white p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-forest">
                    <input type="checkbox" checked={draft.includeLocation} onChange={(e) => setDraft({ ...draft, includeLocation: e.target.checked })} data-testid="studio-include-location" /> Include general area
                  </label>
                  <input value={draft.general_location} onChange={(e) => setDraft({ ...draft, general_location: e.target.value })} disabled={!draft.includeLocation} className={`${inp} mt-2 disabled:opacity-50`} placeholder="Montana" data-testid="studio-location" />
                </div>
              </div>

              <div>
                <span className="text-[11px] uppercase tracking-wide text-charcoal/50">Photos</span>
                <div className="flex gap-2 mt-1">
                  <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPhoto())} className={inp} placeholder="Paste an image URL (https://…)" data-testid="studio-photo-url" />
                  <button onClick={addPhoto} className="bg-forest text-bone px-4 text-sm font-semibold shrink-0" data-testid="studio-photo-add"><Plus size={15} /></button>
                </div>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="studio-photos">
                    {photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 bg-charcoal overflow-hidden">
                        <img src={p} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-charcoal/80 text-white rounded-full p-0.5" aria-label="Remove photo"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div data-testid="studio-preview">
              <p className="text-[11px] uppercase tracking-wide text-charcoal/50 mb-2">Preview — this is how it appears in The Archive</p>
              <div className="max-w-xs"><StoryCard s={story} /></div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 p-5 border-t border-saddle/20 sticky bottom-0 bg-paper">
          <button onClick={() => setPreview((p) => !p)} className="text-sm font-semibold text-forest hover:text-blaze inline-flex items-center gap-1.5" data-testid="studio-preview-toggle">
            {preview ? <><PenLine size={15} /> Keep editing</> : <><Eye size={15} /> Preview</>}
          </button>
          <button onClick={publish} disabled={busy} className="bg-blaze text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-light transition-colors disabled:opacity-60 inline-flex items-center gap-2" data-testid="studio-publish">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Globe size={15} />} Publish to Archive
          </button>
        </div>
      </div>
    </div>
  );
}
